/**
 * LearnChunking.jsx — displays a passage in fixed-width phrase chunks
 * (2–5 words at a time), advancing automatically at a pace derived from
 * the target WPM. Trains eye-span expansion — the single most evidence-
 * backed lever for faster reading with retained comprehension.
 *
 * Chunk-size unlocks come from the backend (`chunk_size_unlocked` on
 * /api/learn/progress). Users start at 2-word chunks; wider chunks
 * unlock as their comprehension-adjusted WPM climbs.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers, Play, Pause, RotateCcw, ArrowLeft, ChevronDown, X as XIcon } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { readCustomPassage } from '../utils/learnCustomPassage';
import LearnQuiz from '../components/LearnQuiz';
import QuitDrillDialog from '../components/QuitDrillDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LearnChunking = () => {
  const navigate = useNavigate();
  const [passages, setPassages] = useState([]);
  const [passageIdx, setPassageIdx] = useState(0);
  const [chunkSize, setChunkSize] = useState(2);
  const [maxChunk, setMaxChunk] = useState(2);
  const [wpm, setWpm] = useState(300);
  const [state, setState] = useState('setup'); // setup|running|paused|done|quiz
  const [chunkIdx, setChunkIdx] = useState(0);
  const [quitOpen, setQuitOpen] = useState(false);
  const [savingQuit, setSavingQuit] = useState(false);
  const startedAt = useRef(0);
  const durationRef = useRef(0);
  const timerRef = useRef(null);

  // Bootstrap: fetch passage bank + user's unlocked chunk width.
  // If a custom passage is stashed in sessionStorage (from the
  // "Bring your own passage" flow), that becomes the sole passage.
  useEffect(() => {
    const custom = readCustomPassage();
    if (custom) setPassages([custom]);
    (async () => {
      try {
        if (!custom) {
          const pr = await authFetch(`${BACKEND_URL}/api/learn/passages`);
          if (pr.ok) { const d = await pr.json(); setPassages(d.passages || []); }
        }
        const gp = await authFetch(`${BACKEND_URL}/api/learn/progress`);
        if (gp.ok) { const p = await gp.json(); setMaxChunk(p.chunk_size_unlocked || 2); }
      } catch { /* non-fatal */ }
    })();
  }, []);

  const passage = passages[passageIdx];
  const chunks = React.useMemo(() => {
    if (!passage) return [];
    const words = passage.text.split(/\s+/).filter(Boolean);
    const out = [];
    for (let i = 0; i < words.length; i += chunkSize) {
      out.push(words.slice(i, i + chunkSize).join(' '));
    }
    return out;
  }, [passage, chunkSize]);

  // Chunks per minute = wpm / chunkSize. Interval per chunk (ms) =
  // 60000 / (wpm / chunkSize).
  const intervalMs = React.useMemo(() => Math.max(120, Math.round(60000 / (wpm / chunkSize))), [wpm, chunkSize]);

  useEffect(() => {
    if (state !== 'running') return;
    timerRef.current = setInterval(() => {
      setChunkIdx((i) => {
        if (i + 1 >= chunks.length) {
          durationRef.current = Date.now() - startedAt.current;
          clearInterval(timerRef.current);
          setState('quiz');
          return i;
        }
        return i + 1;
      });
    }, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [state, intervalMs, chunks.length]);

  // Iter 81 — after passages are loaded, try to hydrate an existing
  // chunking bookmark for the currently-selected passage so the reader
  // can resume from where they left off.
  const [resumeBookmark, setResumeBookmark] = useState(null);
  useEffect(() => {
    if (!passages.length) return;
    const pid = passages[passageIdx]?.passage_id;
    if (!pid) return;
    let alive = true;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/learn/drill-bookmarks`);
        if (!r.ok) return;
        const d = await r.json();
        const hit = (d.bookmarks || []).find(
          (b) => b.drill_kind === 'chunking' && b.passage_id === pid,
        );
        if (alive) setResumeBookmark(hit || null);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [passages, passageIdx]);

  const resumeFromBookmark = () => {
    if (!resumeBookmark) return;
    setChunkSize(resumeBookmark.chunk_size);
    setWpm(resumeBookmark.wpm);
    setChunkIdx(resumeBookmark.chunk_idx || 0);
    startedAt.current = Date.now();
    setState('running');
    // Clear the bookmark server-side — user is picking it back up now.
    authFetch(
      `${BACKEND_URL}/api/learn/drill-bookmarks/chunking/${resumeBookmark.passage_id}`,
      { method: 'DELETE' },
    ).catch(() => {});
    setResumeBookmark(null);
  };

  const start = () => {
    setChunkIdx(0);
    startedAt.current = Date.now();
    setState('running');
  };
  const pause = () => {
    durationRef.current += Date.now() - startedAt.current;
    setState('paused');
    clearInterval(timerRef.current);
  };
  const resume = () => {
    startedAt.current = Date.now();
    setState('running');
  };
  const reset = () => {
    setState('setup');
    setChunkIdx(0);
    durationRef.current = 0;
    clearInterval(timerRef.current);
  };

  if (!passage) {
    return (
      <main className="sharp-bg min-h-screen flex items-center justify-center text-white/60" data-testid="learn-chunking-loading">
        Loading passages…
      </main>
    );
  }

  return (
    <main className="sharp-bg min-h-screen max-w-3xl mx-auto px-4 py-6 pb-32" data-testid="learn-chunking-page">
      <button
        onClick={() => navigate('/learn')}
        className="text-xs text-white/50 hover:text-white/80 mb-4 inline-flex items-center gap-1.5"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to LEARN
      </button>

      <header className="mb-6 flex items-center gap-3">
        <Layers className="w-6 h-6 text-cyan-300" />
        <div>
          <p className="eyebrow text-cyan-300 mb-0.5">Drill · Chunking</p>
          <h1 className="font-display text-3xl font-black text-white tracking-tight leading-none">
            Read in phrases<span className="text-cyan-300">.</span>
          </h1>
        </div>
      </header>

      {state === 'setup' && (
        <div className="space-y-5">
          {resumeBookmark && (
            <div
              className="rounded-2xl bg-cyan-500/10 border border-cyan-400/40 p-4 flex items-center gap-3"
              data-testid="learn-chunking-resume-banner"
            >
              <div className="flex-1 min-w-0">
                <p className="eyebrow text-cyan-200 mb-0.5">Resume where you left off</p>
                <p className="text-sm text-white font-medium truncate">
                  {resumeBookmark.progress_pct}% into &quot;{resumeBookmark.passage_title || 'this passage'}&quot; · {resumeBookmark.wpm} wpm · {resumeBookmark.chunk_size}-word chunks
                </p>
              </div>
              <button
                type="button"
                onClick={resumeFromBookmark}
                className="rounded-lg bg-cyan-400 hover:bg-cyan-300 text-slate-950 px-3 py-2 text-xs font-black uppercase tracking-widest"
                data-testid="learn-chunking-resume-btn"
              >
                Resume
              </button>
            </div>
          )}
          <div className="rounded-2xl bg-[#12131A] border border-white/10 p-5">
            <p className="eyebrow text-white/50 mb-2">Passage</p>
            <div className="relative">
              <select
                value={passageIdx}
                onChange={(e) => setPassageIdx(Number(e.target.value))}
                className="w-full appearance-none bg-black/40 border border-white/15 rounded-lg pl-3 pr-9 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-400/60"
                data-testid="learn-chunking-passage-select"
              >
                {passages.map((p, i) => (
                  <option key={p.passage_id} value={i}>
                    {p.title} — {p.author} ({p.word_count} words)
                  </option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            </div>
          </div>

          <div className="rounded-2xl bg-[#12131A] border border-white/10 p-5">
            <p className="eyebrow text-white/50 mb-3">Chunk size</p>
            <div className="grid grid-cols-4 gap-2">
              {[2, 3, 4, 5].map((n) => {
                const locked = n > maxChunk;
                return (
                  <button
                    key={n}
                    type="button"
                    disabled={locked}
                    onClick={() => setChunkSize(n)}
                    className={`px-3 py-2.5 rounded-lg border text-sm font-black tracking-tight transition-colors ${
                      chunkSize === n
                        ? 'bg-cyan-500 border-cyan-400 text-slate-950'
                        : locked
                          ? 'bg-white/[0.02] border-white/10 text-white/25 cursor-not-allowed'
                          : 'bg-white/[0.03] border-white/15 text-white/80 hover:bg-white/[0.08]'
                    }`}
                    data-testid={`learn-chunking-size-${n}`}
                  >
                    {n}-word{locked ? ' 🔒' : ''}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-white/40 mt-3">
              Highest unlocked: <span className="text-cyan-300 font-black">{maxChunk}-word</span> · widen by climbing adjusted WPM.
            </p>
          </div>

          <div className="rounded-2xl bg-[#12131A] border border-white/10 p-5">
            <p className="eyebrow text-white/50 mb-2">Pace (WPM)</p>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={150}
                max={600}
                step={10}
                value={wpm}
                onChange={(e) => setWpm(Number(e.target.value))}
                className="flex-1 accent-cyan-400"
                data-testid="learn-chunking-wpm-slider"
              />
              <span className="w-16 text-right font-display text-2xl font-black text-cyan-300 tabular-nums">
                {wpm}
              </span>
            </div>
          </div>

          <button
            onClick={start}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 px-4 py-3.5 text-sm font-black tracking-tight transition-colors"
            data-testid="learn-chunking-start"
          >
            <Play className="w-4 h-4" /> Start drill
          </button>
        </div>
      )}

      {(state === 'running' || state === 'paused') && (
        <div className="space-y-5">
          {/* Reader stage — the current chunk is the ONLY thing on screen,
              so the reader has no choice but to fixate on the phrase. */}
          <div
            className="min-h-[45vh] flex items-center justify-center rounded-2xl bg-gradient-to-br from-slate-900 via-slate-950 to-black border border-cyan-500/20 p-8"
            data-testid="learn-chunking-stage"
          >
            <p
              className="font-display text-4xl sm:text-6xl font-black text-white tracking-tight text-center leading-tight"
              data-testid="learn-chunking-chunk"
            >
              {chunks[chunkIdx] || '…'}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {state === 'running' ? (
              <button
                onClick={pause}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-white/10 hover:bg-white/15 text-white px-4 py-2.5 text-sm font-black"
                data-testid="learn-chunking-pause"
              >
                <Pause className="w-4 h-4" /> Pause
              </button>
            ) : (
              <button
                onClick={resume}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 px-4 py-2.5 text-sm font-black"
                data-testid="learn-chunking-resume"
              >
                <Play className="w-4 h-4" /> Resume
              </button>
            )}
            <button
              onClick={reset}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 text-white/70 px-4 py-2.5 text-sm font-black"
              data-testid="learn-chunking-reset"
              title="Restart passage"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <button
              onClick={() => setQuitOpen(true)}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-500/15 hover:bg-red-500/25 border border-red-500/40 text-red-200 px-4 py-2.5 text-xs font-black uppercase tracking-widest"
              data-testid="learn-chunking-quit"
              title="Quit drill and go back"
            >
              <XIcon className="w-4 h-4" /> Quit
            </button>
          </div>

          {/* Live speed adjuster — the interval effect above re-runs
              whenever `wpm` changes, so hitting +/- during the drill
              swaps the pace on the very next tick without restarting
              the passage. Bounded to the same 150–600 window as setup. */}
          <div className="flex items-center gap-2 rounded-xl bg-black/30 border border-cyan-500/20 px-3 py-2">
            <span className="text-[10px] uppercase tracking-widest text-cyan-300/80 font-black">Pace</span>
            <button
              onClick={() => setWpm((w) => Math.max(150, w - 10))}
              className="w-9 h-9 rounded-lg bg-white/[0.05] hover:bg-white/[0.12] border border-white/10 text-white text-lg font-black leading-none"
              data-testid="learn-chunking-wpm-minus"
              aria-label="Slower"
            >
              −
            </button>
            <span
              className="flex-1 text-center font-display text-2xl font-black text-cyan-300 tabular-nums"
              data-testid="learn-chunking-wpm-live"
            >
              {wpm}
            </span>
            <button
              onClick={() => setWpm((w) => Math.min(600, w + 10))}
              className="w-9 h-9 rounded-lg bg-white/[0.05] hover:bg-white/[0.12] border border-white/10 text-white text-lg font-black leading-none"
              data-testid="learn-chunking-wpm-plus"
              aria-label="Faster"
            >
              +
            </button>
            <span className="text-[10px] uppercase tracking-widest text-white/40">WPM</span>
          </div>

          <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
            <div
              className="h-full bg-cyan-400 transition-all"
              style={{ width: `${chunks.length ? Math.round(100 * (chunkIdx + 1) / chunks.length) : 0}%` }}
              data-testid="learn-chunking-progress"
            />
          </div>
        </div>
      )}

      {state === 'quiz' && (
        <LearnQuiz
          passage={passage}
          drillKind="chunking"
          durationMs={durationRef.current}
          chunkSize={chunkSize}
          onDone={() => setState('done')}
        />
      )}

      {state === 'done' && (
        <div className="mt-5 flex gap-3">
          <button
            onClick={reset}
            className="flex-1 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 px-4 py-3 text-sm font-black"
            data-testid="learn-chunking-again"
          >
            Run another drill
          </button>
          <button
            onClick={() => navigate('/learn')}
            className="rounded-lg bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 text-white/70 px-4 py-3 text-sm font-black"
          >
            Back to LEARN
          </button>
        </div>
      )}

      <QuitDrillDialog
        open={quitOpen}
        onOpenChange={setQuitOpen}
        progressPct={chunks.length ? Math.round(100 * chunkIdx / chunks.length) : 0}
        drillLabel="chunking drill"
        saving={savingQuit}
        onQuitOnly={() => {
          clearInterval(timerRef.current);
          setQuitOpen(false);
          navigate('/learn');
        }}
        onSaveAndQuit={async () => {
          if (!passage) return;
          setSavingQuit(true);
          try {
            await authFetch(`${BACKEND_URL}/api/learn/drill-bookmarks/save`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                drill_kind: 'chunking',
                passage_id: passage.passage_id,
                passage_title: passage.title || '',
                wpm,
                chunk_size: chunkSize,
                chunk_idx: chunkIdx,
                total_chunks: chunks.length || 1,
              }),
            });
          } catch {
            /* non-fatal — still leave */
          } finally {
            clearInterval(timerRef.current);
            setSavingQuit(false);
            setQuitOpen(false);
            navigate('/learn?resumed=1');
          }
        }}
      />
    </main>
  );
};

export default LearnChunking;
